import AppStoreBadgeArt from '@gearghost/brand-assets/email/apple-app-store-badge-black-360.png';
import styles from './appStoreBadge.module.scss';

/**
 * AppStoreBadge — Apple's official "Download on the App Store" badge, linked
 * to the live iOS listing. The ONE place the listing URL lives on the web:
 * the footer, the home page's header and its hero carousel
 * (WhatsNewCarousel) all render this.
 *
 * It is Apple's black artwork, the same file the launch email uses, and not
 * the teal outline drawing the footer used to carry: solid black and white
 * reads at a glance against the site's teal, and it is what Apple's marketing
 * rules ask for (developer.apple.com/app-store/marketing/guidelines, read
 * 2026-09-07 for the launch email and again 2026-10-01). Those rules are why
 * this component does so little:
 *   - "Don't modify, angle, or animate the App Store badge": no recolor,
 *     border, glow or tilt, so its weight comes from size and clear space;
 *   - it is at least 40px tall, with clear space around it.
 *
 * WHAT THE RULES DO NOT SAY. Apple's "product images" are its own pictures
 * of its devices, which are never cropped, animated or made into buttons.
 * The web uses none: the phone frame in the home carousel is plain CSS. An
 * app's own screenshot is not a product image, and nothing on that page
 * keeps one from linking to the listing, which the carousel's do. (This
 * comment once said screenshots are never links; that stretched the
 * product-image rule past what it says.)
 *
 * THE HOME CAROUSEL'S BADGE rides on a slide, so it travels as the carousel
 * advances (Tyler, 2026-10-01). The artwork itself is not animated or
 * altered; whether a moving slide counts is a judgement, made on purpose.
 *
 * `size` is the only knob: `sm` for the header, `md` for the footer, `lg`
 * where the badge is the point of the section.
 *
 * Android has no listing yet; its footer badge stays an unlinked
 * "Coming Soon" and has no component until there is a URL to point at.
 */
export const APP_STORE_URL = 'https://apps.apple.com/us/app/gearghost/id6739769982';

/** The badge's accessible name; Apple's own words for the artwork. */
export const APP_STORE_BADGE_LABEL = 'Download on the App Store';

/**
 * A link to the live listing, in a new tab. The badge is one, and so is each
 * screenshot in the home carousel; both come through here so the listing URL
 * and how it opens are written once.
 */
export const AppStoreLink = ({ className, children }) => (
  <a className={className} href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
    {children}
  </a>
);

const AppStoreBadge = ({ size = 'md' }) => (
  <AppStoreLink className={`${styles.badge} ${styles[size]}`}>
    <img src={AppStoreBadgeArt} alt={APP_STORE_BADGE_LABEL} />
  </AppStoreLink>
);

export default AppStoreBadge;
